import { yupResolver } from "@hookform/resolvers/yup";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import LoadingFormNormal from "components/shared-components/LoadingFormNormal";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Switch } from "antd";
import * as yup from "yup";
import RouteLocalitiesTable from "./RouteLocalitiesTable";
import { routeModeList } from "./utils";
import {
  useDetailRouteCollector,
  useUpdateRouteCollector,
} from "hooks/route-collector/useRouteCollector";

const DetailRoute = props => {
  const { isOpenModal, dataDetail, toggleModal, mode, isLevel2 = true } = props;
  const { t, i18n } = useTranslation(["routeCollector"]);
  const { addToast } = useToasts();

  const [addressLevel, setAddressLevel] = useState("all");
  const [addressLevelFilter, setAddressLevelFilter] = useState({
    value: "all",
    label: "Tất cả",
  });
  const [isOpenAddrFilter, setIsOpenAddrFilter] = useState(false);

  // Filter options
  const addressLevelFilterOptions = [
    { value: "all", label: "Tất cả" },
    { value: "2", label: "2 cấp" },
    { value: "3", label: "3 cấp" },
  ];
  const handleAddressLevelFilterChange = selectedOption => {
    setAddressLevelFilter(selectedOption);
    setAddressLevel(selectedOption.value);
  };

  const [isDetail, setIsDetail] = useState(false);

  const getInitDefaultValues = () => {
    return {
      hub_id: null,
      code: "",
      name: "",
      description: "",
    };
  };

  const getInitControlFields = () => {
    return {
      display: {
        hub_id: false,
        code: false,
        name: false,
        description: false,
        btn_save: false,
      },
      disabled: {
        hub_id: false,
        code: false,
        name: false,
        description: false,
      },
    };
  };
  const [controlFields, setControlFields] = useState(getInitControlFields);

  const schema = yup.object().shape({
    hub_id: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.hub_id") })),
    code: yup
      .string()
      .required(t("form.validate.required", { title: t("form.code") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    description: yup
      .string()
      .required(t("form.validate.required", { title: t("form.description") })),
  });

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = mainForm;

  // Detail Route
  const onSuccessDetailRoute = data => {
    reset(getInitDefaultValues());

    switch (mode) {
      case routeModeList.VIEW_ROUTE_DETAIL:
        setValue("name", data.name);
        setValue("code", data.code);
        setValue("description", data.description);

        setControlFields({
          display: {
            ...getInitControlFields().display,
            hub_id: true,
            name: true,
            code: true,
            description: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
            hub_id: true,
            name: true,
            code: true,
            description: true,
          },
        });
        break;
      case routeModeList.EDIT_ROUTE_DETAIL:
        setValue("name", data.name, { shouldValidate: true });
        setValue("code", data.code, { shouldValidate: true });
        setValue("description", data.description, { shouldValidate: true });

        setControlFields({
          display: {
            ...getInitControlFields().display,
            hub_id: true,
            name: true,
            code: true,
            description: true,
            btn_save: true,
          },
          disabled: {
            ...getInitControlFields().disabled,
          },
        });
        break;
      default:
        break;
    }
  };

  const {
    isLoading: isLoadingDetail,
    data: routeDetail,
    refetch: detailRoute,
    remove: resetDetailRoute,
  } = useDetailRouteCollector(
    { routeId: dataDetail?.routeId },
    onSuccessDetailRoute
  );

  // Update Route
  const onSuccessUpdateRoute = _data => {
    toggleModal(mode);
  };

  const onErrorUpdateRoute = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
    if (error.code !== 422) {
      toggleModal(mode);
    }
  };

  const { isLoading: isLoadingUpdate, mutate: updateRoute } =
    useUpdateRouteCollector(onSuccessUpdateRoute, onErrorUpdateRoute);

  useEffect(() => {
    if (!isOpenModal) {
      resetDetailRoute();
      setIsDetail(false);
      setControlFields(getInitControlFields());
      reset(getInitDefaultValues());
      // Reset Filter All
      setAddressLevel("all");
      setAddressLevelFilter({
        value: "all",
        label: "Tất cả",
      });
    }
  }, [isOpenModal]);

  // Action Detail
  useEffect(() => {
    if (isOpenModal) {
      if (dataDetail && dataDetail.routeId && !isDetail) {
        detailRoute();
        setIsDetail(true);
      }
    }
  }, [dataDetail, detailRoute, isDetail, isOpenModal]);

  const onSubmit = values => {
    const data = getValues();

    switch (mode) {
      case routeModeList.EDIT_ROUTE_DETAIL:
        updateRoute({
          routeId: routeDetail?.id,
          data: {
            hub_id: data?.hub_id?.value,
            name: data.name,
            code: data.code,
            description: data.description,
          },
        });
        break;
      default:
        break;
    }
  };

  return (
    <React.Fragment>
      <Modal
        size="xl"
        isOpen={isOpenModal}
        role="dialog"
        autoFocus={true}
        centered={true}
        className="exampleModal"
        toggle={toggleModal}
      >
        <ModalHeader toggle={toggleModal}>
          {mode === routeModeList.VIEW_ROUTE_DETAIL && `${t("title.detail")}`}
          {mode === routeModeList.EDIT_ROUTE_DETAIL && `${t("title.edit")}`}
        </ModalHeader>
        {isLoadingDetail ? (
          <ModalBody>
            <LoadingFormNormal
              countInput="6"
              countButton="1"
              formClass="row-cols-1 row-cols-md-2"
              buttonClass="justify-content-end"
              buttonColClass="col-3"
            />
          </ModalBody>
        ) : (
          <React.Fragment>
            <ModalBody>
              <Row
                className="row-cols-1 row-cols-md-2"
                style={{
                  position: "relative",
                  zIndex: 10,
                }}
              >
                {controlFields.display?.hub_id && (
                  <Col>
                    <FormHubSelect
                      isMyHubOnly
                      hubIdDefault={routeDetail?.hub_id}
                      isRequired
                      mainForm={mainForm}
                      fieldName="hub_id"
                      t={t}
                      title={t("form.hub_id")}
                      isDisabled={controlFields.disabled?.hub_id}
                      addressLevel={isLevel2 ? 2 : 3}
                      key={`hub-select-${isLevel2}`}
                    />
                  </Col>
                )}
                {controlFields.display.code && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="code">
                        {t("form.code")} <span className="text-danger">*</span>
                      </Label>
                      <input
                        disabled={controlFields.disabled.code}
                        type="text"
                        className={`form-control ${formValidate(
                          errors,
                          touchedFields,
                          "code"
                        )}`}
                        placeholder={t("placeholder.enter", {
                          title: t("form.code"),
                        })}
                        {...register("code")}
                      />
                      {errors.code && (
                        <MessageFormValidate error={errors.code.message} />
                      )}
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.name && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="name">
                        {t("form.name")} <span className="text-danger">*</span>
                      </Label>
                      <input
                        disabled={controlFields.disabled.name}
                        type="text"
                        className={`form-control ${formValidate(
                          errors,
                          touchedFields,
                          "name"
                        )}`}
                        placeholder={t("placeholder.enter", {
                          title: t("form.name"),
                        })}
                        {...register("name")}
                      />
                      {errors.name && (
                        <MessageFormValidate error={errors.name.message} />
                      )}
                    </FormGroup>
                  </Col>
                )}

                {controlFields.display.description && (
                  <Col>
                    <FormGroup className="mb-3">
                      <Label htmlFor="description">
                        {t("form.description")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <input
                        disabled={controlFields.disabled.description}
                        type="text"
                        className={`form-control ${formValidate(
                          errors,
                          touchedFields,
                          "description"
                        )}`}
                        placeholder={t("placeholder.enter", {
                          title: t("form.description"),
                        })}
                        {...register("description")}
                      />
                      {errors.description && (
                        <MessageFormValidate
                          error={errors.description.message}
                        />
                      )}
                    </FormGroup>
                  </Col>
                )}
              </Row>
              <Row className="row-cols-1">
                <Col>
                  <RouteLocalitiesTable
                    routeId={dataDetail?.routeId}
                    isLoading={isLoadingDetail}
                    addressLevel={addressLevel}
                    onAddressLevelChange={handleAddressLevelFilterChange}
                    addressLevelFilter={addressLevelFilter}
                    addressLevelFilterOptions={addressLevelFilterOptions}
                    isOpenAddrFilter={isOpenAddrFilter}
                    setIsOpenAddrFilter={setIsOpenAddrFilter}
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Row className="justify-content-end">
                {controlFields.display.btn_save && (
                  <Col className="col-auto">
                    <Button
                      color="primary"
                      className="btn-label"
                      type="button"
                      onClick={() => {
                        handleSubmit(onSubmit)();
                      }}
                      disabled={isLoadingUpdate}
                    >
                      <i className="bx bx-save label-icon"></i>
                      {t("form.update")}
                    </Button>
                  </Col>
                )}
              </Row>
            </ModalFooter>
          </React.Fragment>
        )}
      </Modal>
    </React.Fragment>
  );
};

DetailRoute.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
  dataDetail: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
};

DetailRoute.defaultProps = {
  isOpenModal: false,
  dataDetail: {},
  mode: "",
};

export default DetailRoute;
